
import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import ApiService, { SsoService } from '@/common/api.service';
import { destroyToken, getToken, saveToken, saveLocale } from '@/common/jwt.service';
import { ROLE_PLAYER } from '@/common/userRoles';
import { SSO_URL, CLIENT_ID, REDIRECT_URI, SCOPE } from '@/common/config';

const OAUTH_STATE_STORAGE_KEY = 'oauth_login_state';

export const useAuthStore = defineStore('auth', () => {
  // State
  const errors = ref(null);
  const user = ref({});
  const isAuthenticated = ref(!!getToken());
  const isLoading = ref(false);
  const portal = ref(null);
  const login = ref(null);
  const password = ref(null);
  const domain = ref(null);
  const referer = ref(null);

  // Getters
  const currentUser = computed(() => user.value);
  const userAvatar = computed(() => user.value.avatarURL ? `${SSO_URL}/api/avatar?hash=${user.value.avatarURL}` : '');
  const isAuthorized = computed(() => user.value.roles && user.value.roles.includes(ROLE_PLAYER));
  const isAdmin = computed(() => user.value.roles && user.value.roles.includes(ROLE_PLAYER));
  const locale = computed(() => user.value?.language?.toLowerCase() ?? 'fr');

  // Actions
  function setLoading(loadingState) {
    isLoading.value = loadingState;
  }

  function setError(error) {
    errors.value = error;
  }

  function normalizeQueryParam(value) {
    if (Array.isArray(value)) {
      return value[0] ?? '';
    }
    return typeof value === 'string' ? value : '';
  }

  function generateOauthState() {
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(32);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  function saveOauthState(state) {
    window.sessionStorage.setItem(OAUTH_STATE_STORAGE_KEY, state);
  }

  function consumeOauthState() {
    const state = window.sessionStorage.getItem(OAUTH_STATE_STORAGE_KEY);
    window.sessionStorage.removeItem(OAUTH_STATE_STORAGE_KEY);
    return state;
  }

  async function setUser(userData, finishIsLoading = false) {
    isAuthenticated.value = true;
    user.value = userData;
    errors.value = {};
    saveLocale(userData?.language?.toLowerCase() ?? 'en');

    if (user.value.login) {
      user.value.initials = user.value.firstName[0] + user.value.lastName[0];
    }

    saveToken(user.value.id_token);

    if (finishIsLoading) {
      isLoading.value = false;
    }
  }

  function purgeAuth() {
    destroyToken();
    window.sessionStorage.removeItem(OAUTH_STATE_STORAGE_KEY);
    isAuthenticated.value = false;
    user.value = {};
    errors.value = {};
  }

  function doLogin() {
    let origin = 'vts_player_web';
    if (portal.value != null) {
      origin = portal.value;
    }
    let loginParam = '';
    if (login.value != null) {
      loginParam = `&login=${btoa(login.value)}`;
    }
    const state = generateOauthState();
    saveOauthState(state);
    window.location.href = `${SSO_URL}/authorize?client_id=${CLIENT_ID}&response_type=code&scope=${SCOPE}&redirect_uri=${REDIRECT_URI}&state=${encodeURIComponent(state)}&_origin=${origin}${loginParam}`;
  }

  async function callbackLogin({ code, state }) {
    const normalizedCode = normalizeQueryParam(code);
    const normalizedState = normalizeQueryParam(state);
    const expectedState = consumeOauthState();
    if (!normalizedCode || !expectedState || normalizedState !== expectedState) {
      throw new Error('OAuth state mismatch');
    }
    setLoading(true);
    const { data } = await SsoService.getIdToken(normalizedCode);
    await setUser({ id_token: data.id_token });
  }

  async function customAuthAction(params = {}) {
    if (params.method === 'get') {
      return ApiService.query(`front/v1/public/${params.path}`, params.data, 1);
    }
    return ApiService.post(`front/v1/public/${params.path}`, params.data, 1);
  }

  async function loginForm(params = {}) {
    setLoading(true);
    const { data } = await SsoService.loginForm(params);
    const token = data.data.id_token;
    const response = await SsoService.verifyToken(token);
    const { claims } = response.data;
    await setUser({ ...claims, finishIsLoading: true });
  }

  function doLogout() {
    setLoading(true);
    purgeAuth();
    const currentDomain = window.location.hostname;
    const performURL = new URL(REDIRECT_URI);
    if (currentDomain === performURL.hostname) {
      window.location.href = `${SSO_URL}/logout`;
    } else {
      window.location.reload();
    }
  }

  async function checkAuth() {
    setLoading(true);
    const token = getToken();
    if (token) {
      try {
        const { data } = await SsoService.verifyToken(token);
        const { claims } = data;
        if (login.value && claims.login !== login.value) {
          purgeAuth();
          throw new Error('Login mismatch');
        }
        await setUser({ ...claims, finishIsLoading: true });
        return true;
      } catch (error) {
        purgeAuth();
        throw error;
      }
    }
    purgeAuth();
    setLoading(false);
    return false;
  }

  function updatePassword({ password, newPassword }) {
    return SsoService.updatePassword(password, newPassword);
  }

  function updateLocale(params) {
    return ApiService.post('front/account/locale', params);
  }

  function setPortal(portalData) {
    portal.value = portalData;
  }

  function setLogin(loginData) {
    const base64regex = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{4})([=]{1,2})?$/;
    login.value = base64regex.test(loginData) ? atob(loginData) : loginData;
  }

  function setPassword(pass) {
    const base64regex = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{4})([=]{1,2})?$/;
    password.value = base64regex.test(pass) ? atob(pass) : pass;
  }

  function setReferer(refererData) {
    referer.value = refererData;
  }

  return {
    errors,
    user,
    isAuthenticated,
    isLoading,
    portal,
    login,
    password,
    domain,
    referer,
    currentUser,
    userAvatar,
    isAuthorized,
    isAdmin,
    locale,
    setLoading,
    setError,
    setUser,
    purgeAuth,
    doLogin,
    callbackLogin,
    customAuthAction,
    loginForm,
    doLogout,
    checkAuth,
    updatePassword,
    updateLocale,
    setPortal,
    setLogin,
    setPassword,
    setReferer,
  };
});
